// Post Writer Web Component
import { applyPostStyleTokens, clearPostStyleTokens } from '../utils/post-style-applier.js';

class PostWriter extends HTMLElement {
  constructor() {
    super();
    this.isSubmitting = false;
    this.selectedPrompt = null;
    this.postType = 'text'; // 'text', 'mood', 'music', or 'poll'
    this.groupThreads = [];
    this.usingTemplateEditor = false;
  }

  static get observedAttributes() {
    return ['group-id', 'thread-id'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected) {
      if (name === 'thread-id' || name === 'thread-name') {
        this.updateGroupStreamHint();
      }
      this.updateGroupModeVisibility();
    }
  }

  get groupId() {
    const raw = this.getAttribute('group-id');
    if (!raw) return null;
    const id = parseInt(raw, 10);
    return Number.isNaN(id) ? null : id;
  }

  get threadId() {
    const raw = this.getAttribute('thread-id');
    if (!raw) return null;
    const id = parseInt(raw, 10);
    return Number.isNaN(id) ? null : id;
  }

  isGroupMode() {
    return this.groupId !== null;
  }

  setGroupThreads(threads) {
    this.groupThreads = Array.isArray(threads) ? threads.filter((t) => !t.archived_at) : [];
    if (this.isConnected) {
      this.updateGroupStreamHint();
    }
  }

  getThreadDisplayName() {
    if (!this.threadId) {
      return '📰 Main feed';
    }
    const attrName = this.getAttribute('thread-name');
    if (attrName) {
      return `🧵 ${attrName}`;
    }
    const thread = this.groupThreads.find((t) => t.id === this.threadId);
    return thread ? `🧵 ${thread.name}` : '🧵 This thread';
  }

  renderGroupStreamHint() {
    if (!this.isGroupMode()) {
      return '';
    }
    return `
      <p class="group-stream-hint" aria-live="polite">
        Posting to <strong>${this.escapeHtml(this.getThreadDisplayName())}</strong>
        <span class="group-stream-hint-detail">Use the feed menu above to switch streams.</span>
      </p>
    `;
  }

  updateGroupStreamHint() {
    const hint = this.querySelector('.group-stream-hint strong');
    if (hint) {
      hint.textContent = this.getThreadDisplayName();
    }
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.updateGroupModeVisibility();
    this.updateCustomizationVisibility();
  }

  escapeHtml(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  render() {
    this.innerHTML = `
      <div class="card">
        <div class="card-body">
          <!-- Mood Selector (shown when mood type is selected) -->
          <div class="mood-section">
            <mood-selector id="moodSelector"></mood-selector>
          </div>
          
          <!-- Music Selector (shown when music type is selected) -->
          <div class="music-section">
            <music-selector id="musicSelector"></music-selector>
          </div>
          
          <!-- Poll Editor (shown when poll type is selected) -->
          <div class="poll-section">
            <edit-poll id="editPoll"></edit-poll>
          </div>
          
          <!-- Text Post Form (shown when text type is selected) -->
          <form id="postWriterForm" class="post-form" novalidate>
            <div class="form-group">
              <prompt-picker id="promptPicker"></prompt-picker>
              ${this.renderGroupStreamHint()}
              <input 
                type="text" 
                class="form-control title-input" 
                name="title" 
                placeholder="Post title..."
                maxlength="255"
              />
              <markdown-preview>
                <textarea 
                  class="form-control" 
                  name="content" 
                  placeholder="Write here... (Markdown supported!)"
                  rows="6"
                ></textarea>
              </markdown-preview>
              <div class="template-editor-container" hidden>
                <template-editor id="templateEditor"></template-editor>
              </div>
            </div>
            <div class="alert-container"></div>
          </form>

          <post-style-picker id="postStylePicker" hidden></post-style-picker>

          <div class="form-actions post-form-actions">
            <button type="submit" form="postWriterForm" class="btn btn-primary">
              <span class="btn-text">${this.isGroupMode() ? 'Post' : 'Share Post'}</span>
              <span class="spinner hidden"></span>
            </button>
            <button type="button" class="btn btn-secondary clear-btn">
              Clear
            </button>
          </div>
        </div>
      </div>
    `;

    this.updatePostTypeVisibility();
    this.updateGroupModeVisibility();
    this.updateCustomizationVisibility();
  }

  updateCustomizationVisibility() {
    const stylePicker = this.querySelector('#postStylePicker');
    if (stylePicker) {
      stylePicker.hidden = this.isGroupMode();
    }
  }

  updateGroupModeVisibility() {
    const inGroup = this.isGroupMode();
    const promptPicker = this.querySelector('#promptPicker');
    const titleInput = this.querySelector('.title-input');
    const moodSection = this.querySelector('.mood-section');
    const musicSection = this.querySelector('.music-section');
    const pollSection = this.querySelector('.poll-section');
    const streamHint = this.querySelector('.group-stream-hint');

    if (promptPicker) promptPicker.style.display = inGroup ? 'none' : '';
    if (titleInput) titleInput.style.display = inGroup ? 'none' : '';
    if (streamHint) streamHint.style.display = inGroup ? '' : 'none';
    if (moodSection) moodSection.style.display = inGroup ? 'none' : moodSection.style.display;
    if (musicSection) musicSection.style.display = inGroup ? 'none' : musicSection.style.display;
    if (pollSection) pollSection.style.display = inGroup ? 'none' : pollSection.style.display;

    if (inGroup) {
      this.postType = 'text';
      this.updatePostTypeVisibility();
    }
  }

  attachEventListeners() {
    const form = this.querySelector('.post-form');
    const titleInput = this.querySelector('input[name="title"]');
    const textarea = this.querySelector('textarea[name="content"]');
    const submitBtn = this.querySelector('button[type="submit"]');
    const clearBtn = this.querySelector('.clear-btn');
    const promptPicker = this.querySelector('#promptPicker');
    const markdownPreview = this.querySelector('.post-form markdown-preview');
    if (markdownPreview?.bindTitleInput && titleInput) {
      markdownPreview.bindTitleInput(titleInput);
    }

    // Listen for post type switch requests from child components
    this.addEventListener('switch-post-type', (e) => {
      const type = e.detail.type;
      if (type !== this.postType) {
        this.switchPostType(type);
      }
    });

    // Listen for mood shared events
    const moodSelector = this.querySelector('#moodSelector');
    if (moodSelector) {
      moodSelector.addEventListener('mood-shared', (e) => {
        // Switch back to text mode and refresh the feed
        this.switchPostType('text');
        this.dispatchEvent(new CustomEvent('post-created', {
          detail: e.detail.moodPost,
          bubbles: true
        }));
      });
    }

    // Listen for music shared events
    const musicSelector = this.querySelector('#musicSelector');
    if (musicSelector) {
      musicSelector.addEventListener('music-shared', (e) => {
        // Switch back to text mode and refresh the feed
        this.switchPostType('text');
        this.dispatchEvent(new CustomEvent('post-created', {
          detail: e.detail.musicPost,
          bubbles: true
        }));
      });
    }

    // Listen for poll created events
    const editPoll = this.querySelector('#editPoll');
    if (editPoll) {
      editPoll.addEventListener('poll-created', (e) => {
        // Switch back to text mode and refresh the feed
        this.switchPostType('text');
        this.dispatchEvent(new CustomEvent('post-created', {
          detail: e.detail.pollPost,
          bubbles: true
        }));
      });
    }

    // Enable/disable submit button based on content
    textarea.addEventListener('input', () => {
      const content = textarea.value.trim();
      submitBtn.disabled = content.length === 0;
    });

    // Auto-resize textarea
    textarea.addEventListener('input', () => {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    });

    // Form submission
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    // Clear button
    clearBtn.addEventListener('click', () => {
      this.clearForm();
    });

    // Keyboard shortcuts
    textarea.addEventListener('keydown', (e) => {
      // Ctrl/Cmd + Enter to submit
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (!submitBtn.disabled) {
          this.handleSubmit();
        }
      }
    });

    // Handle prompt selection
    if (promptPicker) {
      promptPicker.addEventListener('prompt-selected', async (e) => {
        const prompt = await this.loadPromptDetails(e.detail.prompt);
        this.selectedPrompt = prompt;

        // If it's a poll prompt, switch to poll mode
        if (prompt.prompt_type === 'poll') {
          this.switchPostType('poll');
          const editPoll = this.querySelector('#editPoll');
          if (editPoll) {
            editPoll.setPromptData(prompt);
          }
          return;
        }

        // For text prompts, populate the form
        this.applyTextPrompt(prompt);
      });
    }

    if (promptPicker) {
      promptPicker.addEventListener('prompt-cleared', () => {
        this.selectedPrompt = null;
        // Clear both title and textarea when prompt is cleared
        titleInput.value = '';
        textarea.value = '';
        textarea.dispatchEvent(new Event('input'));
      });
    }

    // Listen for prompt reuse from other components
    document.addEventListener('prompt-selected', (e) => {
      this.handlePromptReuse(e.detail);
    });

    this._bindPostStylePreview();
  }

  getPostStylePayload() {
    const stylePicker = this.querySelector('#postStylePicker');
    if (!stylePicker || stylePicker.hidden) {
      return {};
    }
    return stylePicker.getStylePayload?.() || {};
  }

  _getPostStylePreviewTarget() {
    switch (this.postType) {
    case 'text': {
      if (this.usingTemplateEditor) return null;
      const markdownPreview = this.querySelector('.post-form markdown-preview');
      return markdownPreview ? { type: 'markdown', el: markdownPreview } : null;
    }
    case 'mood':
      return {
        type: 'element',
        el: this.querySelector('#moodSelector .mood-selector') || this.querySelector('#moodSelector')
      };
    case 'music':
      return {
        type: 'element',
        el: this.querySelector('#musicSelector .music-selector') || this.querySelector('#musicSelector')
      };
    case 'poll':
      return {
        type: 'element',
        el: this.querySelector('#editPoll .edit-poll') || this.querySelector('#editPoll')
      };
    default:
      return null;
    }
  }

  _applyPostStylePreview(style, { flipTextPreview = false } = {}) {
    const target = this._getPostStylePreviewTarget();
    if (!target) return;

    if (target.type === 'markdown') {
      if (flipTextPreview) {
        target.el.setMode('preview');
      }
      if (style) {
        target.el.applyPostStyle(style);
      } else {
        target.el.clearPostStyle();
      }
      return;
    }

    if (!target.el) return;

    if (style) {
      applyPostStyleTokens(target.el, style);
    } else {
      clearPostStyleTokens(target.el);
    }

    if (this.postType === 'music') {
      const musicPreview = this.querySelector('#musicSelector #musicPreview');
      if (musicPreview && musicPreview.style.display !== 'none') {
        musicPreview.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  _bindPostStylePreview() {
    this.addEventListener('post-style-preview-request', () => {
      const stylePicker = this.querySelector('#postStylePicker');
      const style = stylePicker?.getStyle?.() ?? null;
      this._applyPostStylePreview(style, { flipTextPreview: true });
    });

    this.addEventListener('post-style-change', (e) => {
      this._applyPostStylePreview(e.detail?.style ?? null);
    });

    const musicSelector = this.querySelector('#musicSelector');
    if (musicSelector) {
      musicSelector.addEventListener('music-preview-shown', () => {
        const stylePicker = this.querySelector('#postStylePicker');
        const style = stylePicker?.getStyle?.() ?? null;
        if (style) {
          this._applyPostStylePreview(style);
        }
      });
    }
  }

  async handleSubmit() {
    if (this.isSubmitting) return;

    const titleInput = this.querySelector('input[name="title"]');
    const textarea = this.querySelector('textarea[name="content"]');
    const title = titleInput.value.trim();
    const templateEditor = this.querySelector('#templateEditor');
    let content;

    if (this.usingTemplateEditor && templateEditor) {
      if (!templateEditor.hasAnyContent()) {
        this.showAlert('Please write something before posting.', 'error');
        return;
      }
      content = templateEditor.getContent().trim();
    } else {
      content = textarea.value.trim();
      if (!content) {
        this.showAlert('Please write something before posting.', 'error');
        return;
      }
    }

    this.setSubmitting(true);
    this.clearAlerts();

    try {
      let post;
      if (this.isGroupMode()) {
        const thread_id = this.threadId ?? null;
        post = await window.API.request(`/groups/${this.groupId}/posts`, {
          method: 'POST',
          body: JSON.stringify({
            content_markdown: content,
            thread_id
          })
        });
      } else {
        const postData = {
          title: title || '',
          content
        };
        if (this.selectedPrompt) {
          postData.promptId = this.selectedPrompt.id;
        }
        Object.assign(postData, this.getPostStylePayload());
        post = await window.API.posts.create(postData);
      }

      // Clear form but keep last-used post style for the next write
      this.clearForm(false);

      // Show success message
      this.showAlert(this.isGroupMode() ? 'Posted! 🎉' : 'Post shared successfully! 🎉', 'success');

      // Notify feeds (document listener; bubbling alone is unreliable across re-renders)
      document.dispatchEvent(new CustomEvent('post-created', {
        detail: post,
        bubbles: true
      }));

    } catch (error) {
      window.logger.error('Failed to create post:', error);
      const message = error.message || 'Failed to share post. Please try again.';
      this.showAlert(message, 'error');

      const stylePicker = this.querySelector('#postStylePicker');
      if (stylePicker?.getStyle?.() && stylePicker.showError) {
        stylePicker.showError(message);
      }
    } finally {
      this.setSubmitting(false);
    }
  }

  setSubmitting(isSubmitting) {
    this.isSubmitting = isSubmitting;
    const submitBtn = this.querySelector('button[type="submit"]');
    const btnText = this.querySelector('.btn-text');
    const spinner = this.querySelector('.spinner');
    const textarea = this.querySelector('textarea');

    if (isSubmitting) {
      submitBtn.disabled = true;
      btnText.textContent = 'Sharing...';
      spinner.classList.remove('hidden');
      textarea.disabled = true;
    } else {
      submitBtn.disabled = textarea.value.trim().length === 0;
      btnText.textContent = this.isGroupMode() ? 'Post' : 'Share Post';
      spinner.classList.add('hidden');
      textarea.disabled = false;
    }
  }

  switchPostType(type) {
    if (type === this.postType) {
      return;
    }
    this.postType = type;
    this.updatePostTypeVisibility();
  }

  updatePostTypeVisibility() {
    const sections = {
      text: this.querySelector('.post-form'),
      mood: this.querySelector('.mood-section'),
      music: this.querySelector('.music-section'),
      poll: this.querySelector('.poll-section')
    };

    for (const [type, section] of Object.entries(sections)) {
      if (section) {
        section.style.display = type === this.postType ? 'block' : 'none';
      }
    }

    const formActions = this.querySelector('.post-form-actions');
    if (formActions) {
      formActions.style.display = this.postType === 'text' ? 'flex' : 'none';
    }

    this.querySelectorAll('post-type-selector').forEach((selector) => {
      if (selector.setSelectedType) {
        selector.setSelectedType(this.postType);
      } else {
        selector.setAttribute('selected-type', this.postType);
      }
    });
  }

  refreshPostsFeed() {
    // Find and refresh the posts feed component
    const postsFeed = document.querySelector('posts-feed');
    if (postsFeed && postsFeed.load) {
      postsFeed.load();
    }

    // Also refresh activity panel if it exists
    const activityPanel = document.querySelector('activity-panel');
    if (activityPanel && activityPanel.loadFriendActivities) {
      activityPanel.loadFriendActivities();
    }
  }

  async loadPromptDetails(promptData) {
    if (!promptData) {
      return promptData;
    }

    let prompt = promptData;

    if (prompt.id && window.API?.prompts?.getById) {
      try {
        const response = await window.API.prompts.getById(prompt.id);
        if (response?.prompt) {
          prompt = response.prompt;
        }
      } catch (error) {
        window.logger?.warn('Could not fetch full prompt details:', error);
      }
    }

    return {
      id: prompt.id,
      title: prompt.title,
      body: prompt.body,
      prompt_type: prompt.prompt_type || 'text',
      template_type: prompt.template_type || 'simple',
      template_structure: prompt.template_structure || null
    };
  }

  async handlePromptReuse(promptData) {
    // If it's a poll prompt, switch to poll mode
    if (promptData.prompt_type === 'poll') {
      this.switchPostType('poll');
      this.selectedPrompt = promptData;

      // Update the prompt picker to show the selected prompt
      const promptPicker = this.querySelector('#promptPicker');
      if (promptPicker && promptPicker.setSelectedPrompt) {
        promptPicker.setSelectedPrompt(this.selectedPrompt);
      }

      // Set prompt data in edit-poll component
      const editPoll = this.querySelector('#editPoll');
      if (editPoll) {
        editPoll.setPromptData(promptData);
      }
      return;
    }

    promptData = await this.loadPromptDetails(promptData);

    // For text prompts, switch to text mode if not already
    if (this.postType !== 'text') {
      this.switchPostType('text');
    }

    // Set the selected prompt
    this.selectedPrompt = promptData;

    // Update the prompt picker to show the selected prompt
    const promptPicker = this.querySelector('#promptPicker');
    if (promptPicker && promptPicker.setSelectedPrompt) {
      promptPicker.setSelectedPrompt(this.selectedPrompt);
    }

    this.applyTextPrompt(this.selectedPrompt);
  }

  applyTextPrompt(prompt) {
    const titleInput = this.querySelector('input[name="title"]');
    const textarea = this.querySelector('textarea[name="content"]');
    const markdownPreview = this.querySelector('markdown-preview');
    const templateContainer = this.querySelector('.template-editor-container');
    const templateEditor = this.querySelector('#templateEditor');
    const submitBtn = this.querySelector('button[type="submit"]');

    if (titleInput && prompt.title) {
      titleInput.value = prompt.title;
    }

    const useStructured = prompt.template_type
      && prompt.template_type !== 'simple'
      && prompt.template_structure;

    this.usingTemplateEditor = Boolean(useStructured);

    if (useStructured && templateEditor && templateContainer) {
      templateContainer.hidden = false;
      if (markdownPreview) markdownPreview.hidden = true;
      if (textarea) {
        textarea.disabled = true;
      }
      templateEditor.setTemplate(prompt.template_type, prompt.template_structure);
      if (submitBtn) submitBtn.disabled = false;
      return;
    }

    if (templateContainer) templateContainer.hidden = true;
    if (markdownPreview) markdownPreview.hidden = false;
    if (textarea) {
      textarea.disabled = false;
    }

    if (textarea && prompt.body) {
      textarea.value = prompt.body;
      textarea.dispatchEvent(new Event('input'));
      textarea.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    } else if (textarea) {
      textarea.focus();
    }
  }

  clearForm(clearStyle = true) {
    const titleInput = this.querySelector('input[name="title"]');
    const textarea = this.querySelector('textarea[name="content"]');
    const promptPicker = this.querySelector('#promptPicker');
    const moodSelector = this.querySelector('#moodSelector');
    const musicSelector = this.querySelector('#musicSelector');
    const editPoll = this.querySelector('#editPoll');

    // Clear text post fields
    if (titleInput) titleInput.value = '';
    if (textarea) {
      textarea.value = '';
      textarea.style.height = 'auto';
      textarea.disabled = false;
    }
    if (promptPicker && promptPicker.clearPrompt) {
      promptPicker.clearPrompt();
    }
    this.selectedPrompt = null;
    this.usingTemplateEditor = false;

    const templateContainer = this.querySelector('.template-editor-container');
    const markdownPreview = this.querySelector('markdown-preview');
    const templateEditor = this.querySelector('#templateEditor');
    const stylePicker = this.querySelector('#postStylePicker');
    if (templateContainer) templateContainer.hidden = true;
    if (markdownPreview) markdownPreview.hidden = false;
    if (templateEditor) templateEditor.setTemplate('simple', null);
    if (clearStyle && stylePicker?.clearStyle) stylePicker.clearStyle();
    if (!clearStyle && stylePicker?.loadSavedStylesAndDefault) {
      stylePicker.loadSavedStylesAndDefault();
    }

    // Clear mood selector if in mood mode
    if (moodSelector && moodSelector.reset) {
      moodSelector.reset();
    }

    // Clear music selector if in music mode
    if (musicSelector && musicSelector.reset) {
      musicSelector.reset();
    }

    // Clear poll editor if in poll mode
    if (editPoll && editPoll.clearPoll) {
      editPoll.clearPoll();
    }

    // Reset submit button state
    const submitBtn = this.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
    }

    // Focus appropriate input
    if (this.postType === 'text' && textarea) {
      textarea.focus();
    }
  }

  showAlert(message, type = 'info') {
    const container = this.querySelector('.alert-container');
    const alert = document.createElement('div');
    alert.className = `alert alert-${type}`;
    alert.textContent = message;

    container.appendChild(alert);
    alert.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    const dismissMs = type === 'error' ? 10000 : 5000;
    setTimeout(() => {
      if (alert.parentNode) {
        alert.remove();
      }
    }, dismissMs);
  }

  clearAlerts() {
    const container = this.querySelector('.alert-container');
    container.innerHTML = '';
  }
}

// Register the custom element
customElements.define('post-writer', PostWriter);
